/**
 * The block view state represents the state of an individual block in Insights. It corresponds 1-to-1 with
 * analytics query view state that is currently in Apollo. The goal is to eventually move all of that state out
 * of Apollo and put it inside Redux in this module.
 *
 * For more details about view state, see the definitions in client/insights/analytics-dashboards/details/view-state/ViewStateTypes.ts
 */
import {objectFromEntries} from '@benchling/js-utils';

import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {RegressionState} from 'ui-library/charting/statistics/regression/RegressionTypes';

import {filterAnalyticsQueryViewStateIds} from './AnalyticsQueryViewState';
import {IdDataRow, MaskingBlockViewState} from './block-view-state/useMaskedBlockViewState';
import {InsightsBlockViewStateActionType} from './BlockViewStateReduxActions';

export interface BlockViewState {
  maskingState: MaskingBlockViewState<IdDataRow> | null;
  // Null when there's no regression in the block, either because we're showing a chart that doesn't support
  // regressions or haven't selected a regression type on a regression supported chart type.
  regressionState: RegressionState | null;
}

export interface BlockViewStates {
  [blockViewStateId: string]: BlockViewState;
}

export const DEFAULT_BLOCK_VIEW_STATE: BlockViewState = {
  maskingState: null,
  regressionState: null,
};

export const INITIAL_BLOCK_VIEW_STATES: BlockViewStates = {};

export class BlockViewStateSelector {
  constructor(readonly state: BenchlingState) {
    this.state = state;
  }

  getBlockViewState(blockViewStateId: string): BlockViewState {
    return this.state.ui.insights.blockViewStates[blockViewStateId] ?? DEFAULT_BLOCK_VIEW_STATE;
  }

  getBlockViewStates(blockViewStateIds: Array<string>): BlockViewStates {
    return objectFromEntries(
      blockViewStateIds.map((blockViewStateId) => [
        blockViewStateId,
        this.getBlockViewState(blockViewStateId),
      ])
    );
  }

  getMaskingState(blockViewStateId: string): MaskingBlockViewState<IdDataRow> | null {
    return this.getBlockViewState(blockViewStateId).maskingState;
  }

  getRegressionState(blockViewStateId: string): RegressionState | null {
    return this.getBlockViewState(blockViewStateId).regressionState;
  }
}

export default function reduceBlockViewStates(
  state: BlockViewStates = INITIAL_BLOCK_VIEW_STATES,
  action: BenchlingAction
): BlockViewStates {
  switch (action.type) {
    case InsightsBlockViewStateActionType.INSIGHTS_UPDATE_ALL_BLOCK_VIEW_STATES_FOR_BLOCK: {
      const {analyticsQueryId, blockViewStateUpdate} = action;
      const blockViewStateIds = Object.keys(state);
      const blockViewStateIdsToUpdate = filterAnalyticsQueryViewStateIds(blockViewStateIds, analyticsQueryId);

      // eslint-disable-next-line benchlint/no-spread-reduce
      return blockViewStateIdsToUpdate.reduce(
        (previousState, blockViewStateId) => ({
          ...previousState,
          [blockViewStateId]: {
            ...previousState[blockViewStateId],
            ...blockViewStateUpdate,
          },
        }),
        state
      );
    }
    case InsightsBlockViewStateActionType.INSIGHTS_UPDATE_BLOCK_VIEW_STATE: {
      const {blockViewStateId, blockViewStateUpdate} = action;

      return {
        ...state,
        [blockViewStateId]: {
          ...state[blockViewStateId],
          ...blockViewStateUpdate,
        },
      };
    }
    default:
      return state;
  }
}
